import { useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';

const SESSION_TIMEOUT = 4 * 60 * 60 * 1000; // 4 hours
const WARNING_TIME = 10 * 60 * 1000; // 10 minutes before timeout

/**
 * Hook to manage session timeout with automatic logout after inactivity
 * @param {Function} logout - Logout function from AuthContext
 * @param {boolean} isLoggedIn - Whether the user is currently logged in
 */
export const useSessionTimeout = (logout, isLoggedIn) => {
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);

  const resetTimer = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);

    if (!isLoggedIn) return;

    warningRef.current = setTimeout(() => {
      toast.warning('Your session will expire in 10 minutes due to inactivity.', {
        autoClose: 10000,
        toastId: 'session-warning',
      });
    }, SESSION_TIMEOUT - WARNING_TIME);

    timeoutRef.current = setTimeout(() => {
      toast.error('Session expired due to inactivity.', {
        autoClose: 5000,
        toastId: 'session-expired',
      });
      logout();
    }, SESSION_TIMEOUT);
  }, [logout, isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => window.addEventListener(event, resetTimer));
    resetTimer();

    return () => {
      events.forEach(event => window.removeEventListener(event, resetTimer));
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
    };
  }, [resetTimer, isLoggedIn]);
};

export default useSessionTimeout;
